// UI strings. Placeholders look like {name}. Plurals use .one / .few / .many / .other suffixes (see lib/i18n.ts).
export const en = {
  'app.tagline': 'Urgent or important? Sort it out.',
  'nav.back': 'Back',
  'nav.done': 'Done',
  'nav.settings': 'Settings',
  'common.close': 'Close',
  'common.dismiss': 'Dismiss',
  'common.offline': "You're offline – changes won't be saved",

  'page.matrix': 'Matrix',
  'page.done': 'Done',
  'page.settings': 'Settings',
  'page.signIn': 'Sign in',

  'quadrant.do.label': 'Do',
  'quadrant.do.hint': 'Urgent & important',
  'quadrant.do.action': 'Do it now',
  'quadrant.schedule.label': 'Schedule',
  'quadrant.schedule.hint': 'Important, not urgent',
  'quadrant.schedule.action': 'Decide when to do it',
  'quadrant.delegate.label': 'Delegate',
  'quadrant.delegate.hint': 'Urgent, not important',
  'quadrant.delegate.action': 'Who can do it for you?',
  'quadrant.delete.label': 'Delete',
  'quadrant.delete.hint': 'Neither urgent nor important',
  'quadrant.delete.action': 'Drop it',
  'quadrant.empty': 'Nothing here',
  'quadrant.emptyTitle': 'Nothing in {quadrant}',
  'quadrant.emptyBody': 'Tasks you add here will show up in priority order.',

  'axis.urgent': 'Urgent',
  'axis.notUrgent': 'Not urgent',
  'axis.important': 'Important',
  'axis.notImportant': 'Not important',
  'board.urgent': '← Urgent',
  'board.notUrgent': 'Not urgent →',
  'board.important': 'Important ↑',
  'board.notImportant': 'Not important ↓',
  'board.empty': 'Add a task above, then drag it to where it belongs.',
  'board.cardLabel': '{title}. Arrow keys move it, Enter edits it.',
  'board.hasNotes': 'Has notes',

  'date.yesterday': 'Yesterday',
  'date.today': 'Today',
  'date.tomorrow': 'Tomorrow',
  'due.overdue': 'Overdue · {date}',
  'due.dueToday': 'Due today',
  'due.title': 'Due {date}',
  'due.pick': 'Date…',
  'due.label': 'Due date',
  'due.clear': 'Clear due date',

  'composer.open': 'Add a task…',
  'composer.placeholder': 'What needs doing?',
  'composer.placeholderIn': 'Add to {quadrant}…',
  'composer.pickQuadrant': 'Pick a quadrant to add it ↓',
  'composer.added': 'Added to {quadrant}',

  'task.edit': 'Edit task',
  'task.save': 'Save',
  'task.title': 'Title',
  'task.notes': 'Notes',
  'task.due': 'Due',
  'task.quadrant': 'Quadrant',
  'task.complete': 'Complete',
  'task.completeNamed': 'Complete “{title}”',
  'task.delete': 'Delete task',
  'task.confirmDelete': "Delete “{title}”? This can't be undone.",
  'task.deleted': 'Task deleted',
  'task.completed': 'Done: {title}',
  'task.restored': 'Restored: {title}',
  'task.undo': 'Undo',

  'done.count.one': '{count} completed',
  'done.count.other': '{count} completed',
  'done.clearAll': 'Clear all',
  'done.confirmClear': 'Permanently delete all completed tasks ({count})?',
  'done.confirmDelete': 'Delete “{title}” for good?',
  'done.restore': 'Restore “{title}”',
  'done.delete': 'Delete “{title}”',
  'done.emptyTitle': 'Nothing completed yet',
  'done.emptyBody': 'Tick off a task and it lands here.',

  'settings.account': 'Account',
  'settings.signedInAs': 'Signed in as',
  'settings.language': 'Language',
  'settings.languageAuto': 'Device language',
  'settings.languageFooter': '“Device language” follows your phone or browser settings.',
  'settings.passkeys': 'Face ID / passkeys',
  'settings.passkeysFooter': 'Sign in with Face ID or Touch ID instead of typing your password. Set it up on each device you use.',
  'settings.passkeyDefault': 'Passkey',
  'settings.passkeyAdded': 'Added {date}',
  'settings.passkeyLastUsed': 'last used {date}',
  'settings.passkeyRemove': 'Remove passkey',
  'settings.passkeyConfirmRemove': "Remove “{name}”? You'll need your password to sign in on that device.",
  'settings.passkeyAddDevice': 'Add this device',
  'settings.passkeySetUp': 'Set up Face ID',
  'settings.passkeysUnsupported': 'Passkeys need a secure (https) connection and a supported browser.',
  'settings.install': 'Install on iPhone',
  'settings.installStep1': 'In Safari, tap',
  'settings.installShare': 'Share',
  'settings.installStep2': ', then',
  'settings.installAddToHome': 'Add to Home Screen',
  'settings.installStep3': '. It opens full-screen like an app.',
  'settings.signOut': 'Sign out',

  'login.faceId': 'Sign in with Face ID',
  'login.orPassword': 'or with password',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign in',

  'passkey.alreadyRegistered': 'This device already has a passkey for this app.',
  'passkey.failed': 'Something went wrong.',
  'passkey.requestFailed': 'Request failed ({status}).',

  'nav.signIn': 'Sign in',
  'legal.privacy': 'Privacy policy',

  'landing.subtitle': 'An Eisenhower matrix for your tasks – on your phone and on your computer.',
  'landing.cta': 'Create a free account',
  'landing.nameMeaning': '“Lengviau” is Lithuanian for “easier”.',
  'landing.howTitle': 'Four boxes, two questions',
  'landing.howBody': 'For every task, ask: is it urgent? Is it important? The answers put it in one of four boxes, and each box tells you what to do.',
  'landing.origin': 'Named after U.S. President Dwight D. Eisenhower, who noticed that important things are rarely urgent, and urgent things are rarely important.',
  'landing.quickAddTitle': 'Add in two taps',
  'landing.quickAddBody': 'Type the task, tap a box. That’s it.',
  'landing.boardTitle': 'Drag freely on a big screen',
  'landing.boardBody': 'On a computer, drag tasks anywhere – the position is the priority.',
  'landing.faceIdTitle': 'Sign in with Face ID',
  'landing.faceIdBody': 'No passwords to type on your phone.',
  'landing.installTitle': 'Works like an app',
  'landing.installBody': 'On iPhone: Safari → Share → Add to Home Screen.',

  'login.forgot': 'Forgot password?',
  'login.noAccount': 'New here?',
  'login.createAccount': 'Create an account',
  'login.resendConfirmation': 'Didn’t get the confirmation email?',

  'signup.title': 'Create your account',
  'signup.subtitle': 'Free – just an email and a password.',
  'signup.passwordHint': 'At least {count} characters',
  'signup.submit': 'Create account',
  'signup.privacyNote': 'How your data is handled:',
  'signup.haveAccount': 'Already have an account?',

  'password.show': 'Show password',
  'password.hide': 'Hide password',

  'auth.backToSignIn': 'Back to sign in',
  'forgot.title': 'Forgot your password?',
  'forgot.subtitle': 'Enter your email and we’ll send you a link to choose a new one.',
  'forgot.submit': 'Send reset link',
  'reset.title': 'Choose a new password',
  'reset.submit': 'Save password',
  'reset.requestNew': 'Request a new link',
  'resend.title': 'Confirm your email',
  'resend.subtitle': 'Didn’t get the link, or did it expire? We’ll send a new one.',
  'resend.submit': 'Send confirmation link',

  'confirm.banner': 'Confirm your email – we sent a link to {email}.',
  'confirm.daysLeft.one': '{count} day left.',
  'confirm.daysLeft.other': '{count} days left.',
  'confirm.resend': 'Resend',

  'settings.emailUnconfirmed': 'Not confirmed yet',
  'settings.emailPending': 'Waiting for you to confirm {email}',
  'settings.resendConfirmation': 'Resend confirmation link',
  'settings.changeEmail': 'Change email',
  'settings.newEmail': 'New email',
  'settings.currentPassword': 'Current password',
  'settings.changePassword': 'Change password',
  'settings.newPassword': 'New password',
  'settings.save': 'Save',
  'settings.cancel': 'Cancel',
  'settings.deleteAccount': 'Delete account',
  'settings.deleteAccountBody': 'This permanently deletes your account with all your tasks and passkeys. It can’t be undone.',
  'settings.deleteAccountPassword': 'Your password, to confirm',
  'settings.deleteAccountSubmit': 'Delete my account',
  'settings.deleteAccountSure': 'Delete your account and all your tasks for good?',
} satisfies Record<string, string>
